import React from 'react';
import { ConfirmModal } from '../ConfirmModal';
import AddIcon from '@mui/icons-material/Add';

type MultiInputFieldProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
};

export const MultiInputField: React.FC<MultiInputFieldProps> = ({
  value = [],
  onChange,
}) => {
  const [pendingRemoveIndex, setPendingRemoveIndex] = React.useState<
    number | null
  >(null);
  const [error, setError] = React.useState<Record<string, boolean> | null>(
    null
  );

  const updateItem = (index: number, newValue: string) => {
    const updated = [...value];
    updated[index] = newValue;
    onChange?.(updated);
    setError(prev => ({ ...(prev || {}), [index]: newValue.trim() === '' }));
  };

  const handleBlur = (index: number, value: string) => {
    setError(prev => ({ ...(prev || {}), [index]: value.trim() === '' }));
  };

  const addItem = () => onChange?.([...(value || []), '']);

  const confirmRemove = () => {
    if (pendingRemoveIndex !== null) {
      const updated = [...value];
      updated.splice(pendingRemoveIndex, 1);
      onChange?.(updated);
      setPendingRemoveIndex(null);
    }
  };

  const handleCancelDeleteSound = () => {
    setPendingRemoveIndex(null);
  };

  const getErrorMessage = (i: number): string | undefined => {
    if (error && typeof error === 'object' && error[i]) {
      return 'This field cannot be empty.';
    }
    return undefined;
  };

  return (
    <div className="w-full space-y-2">
      {(value ?? []).map((item, i) => (
        <div key={i}>
          <div className="flex items-center gap-2">
            <input
              className={`flex-1 border focus:outline-none p-3 rounded-xl text-medium ${
                getErrorMessage(i) ? 'border-red-500' : 'border-gray-300'
              }`}
              value={item}
              onChange={e => updateItem(i, e.target.value)}
              onBlur={e => handleBlur(i, e.target.value)}
            />
            <button
              type="button"
              onClick={() => setPendingRemoveIndex(i)}
              className="text-red-500 font-bold hover:text-red-700"
            >
              ✖
            </button>
          </div>
          {getErrorMessage(i) && (
            <div className="text-red-500 text-sm mt-1 italic">
              This field cannot be empty.
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="text-blue-500 text-tiny hover:text-blue-700"
      >
        <span className="flex items-center">
          <AddIcon
            fontSize="medium"
            color="inherit"
            className="cursor-pointer"
          />
          <span className="ml-2">Add Item</span>
        </span>
      </button>

      <ConfirmModal
        open={pendingRemoveIndex !== null}
        title="Confirm Deletion"
        onClose={handleCancelDeleteSound}
        onConfirm={confirmRemove}
        confirmButtonClassName="w-full bg-dangerSecondary hover:bg-danger border border-dangerSecondary text-contrast"
        confirmButtonLabel="Delete"
      >
        <div className="flex flex-col gap-y-md justify-center items-center mt-md">
          <p className="text-center text-3xl">
            Are you sure you want to delete this item? This action cannot be
            undone.
          </p>
        </div>
      </ConfirmModal>
    </div>
  );
};
